import {
  Box,
  Flex,
  InputGroup,
  Input,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import { finleyAssert } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  AddTextButton,
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement, WipConfig } from 'packages/creditAgreements/ui/types';

const _areBindings = (ipt: any): ipt is string[] => {
  if (!Array.isArray(ipt)) {
    return false;
  }
  return ipt.every((el) => typeof el === 'string');
};

const _sanitizeBinding = (binding: string): string => {
  return binding.trim();
};

type EditBindingRowProps = {
  idx: number;
  initialBindingString: string;
  onSave: (newBindingString: string) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
};
const EditBindingRow = ({
  idx,
  initialBindingString,
  onSave,
  onClose,
  isSaving,
}: EditBindingRowProps) => {
  const toast = useToast();
  const [bindingString, setBindingString] = useState<string>(
    initialBindingString,
  );

  return (
    <Tr>
      <Td>{idx + 1}</Td>
      <Td>
        <InputGroup>
          <Input
            type="text"
            value={bindingString}
            onChange={(e) => setBindingString(e.target.value)}
          />
        </InputGroup>
      </Td>
      <Td verticalAlign="top">
        <Flex alignItems="center">
          <Button
            w="100%"
            color={colors.aurora[400]}
            border="2px"
            borderRadius="8px"
            borderColor={colors.aurora[400]}
            backgroundColor="white"
            onClick={() => {
              setBindingString(initialBindingString);
              onClose();
            }}
            mr="24px"
          >
            Cancel
          </Button>
          <Button
            w="100%"
            color="white"
            backgroundColor={colors.aurora[400]}
            isLoading={isSaving}
            isDisabled={_sanitizeBinding(bindingString).length === 0}
            onClick={async () => {
              try {
                await onSave(bindingString);
              } catch (err) {
                setBindingString(initialBindingString);

                toast({
                  ...errorToastConfig,
                  id: 'bindingError',
                  title: 'Error saving binding. Please try again.',
                });
              } finally {
                onClose();
              }
            }}
          >
            Save
          </Button>
        </Flex>
      </Td>
    </Tr>
  );
};

type BaseBindingViewProps = {
  bindingString: string;
  bindingsPropertyKey: string;
  wipConfig?: WipConfig;
  idx: number;
  addBinding: ({ bindingStringInput }: { bindingStringInput: string }) => void;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  query:
    | CreditAgreement.Config.AthenaConfig.Query
    | CreditAgreement.Config.AthenaConfig.RecurringQuery;
  queryType: 'singleQuery' | 'multiQuery';
};

interface AthenaQueryBindingViewProps extends BaseBindingViewProps {
  query: CreditAgreement.Config.AthenaConfig.Query;
  queryType: 'singleQuery';
}
interface AthenaRecurringQueryBindingViewProps extends BaseBindingViewProps {
  query: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  queryType: 'multiQuery';
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
}

const BindingRow = (
  params: AthenaQueryBindingViewProps | AthenaRecurringQueryBindingViewProps,
) => {
  const {
    bindingString,
    wipConfig,
    bindingsPropertyKey,
    idx,
    addBinding,
    presetVariables,
    validationVariables,
    query,
    queryType,
  } = params;

  const toast = useToast();

  const bindingIdxPropertyKey = `${bindingsPropertyKey}[${idx}]`;
  const {
    isOpen: isBindingEditOpen,
    onOpen: onBindingEditOpen,
    onClose: onBindingEditClose,
  } = useDisclosure();

  const [isBindingEditSaving, setIsBindingEditSaving] = useState<boolean>(
    false,
  );

  const saveBinding = useCallback(
    async (newBindingString: string): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(bindingIdxPropertyKey)) {
        return;
      }

      const sanitizedBinding = _sanitizeBinding(newBindingString);
      if (sanitizedBinding.length === 0) {
        return;
      }

      const bindingsCopy = _.cloneDeep(query.bindings);
      bindingsCopy[idx] = sanitizedBinding;

      if (queryType === 'singleQuery') {
        const athenaQueryErrors = athenaValidate.validateAthenaQuery({
          query: query.query.queryString,
          bindings: bindingsCopy,
          variables: [
            ...validationVariables,
            ...presetVariables,
            ...query.variables,
          ],
        });

        if (athenaQueryErrors.length) {
          toast({
            ...errorToastConfig,
            title: 'Bindings Update Error',
            description: athenaQueryErrors.join(', '),
          });
          return;
        }
      }

      if (queryType === 'multiQuery') {
        const athenaRecurringQueryErrors = athenaValidate.validateAthenaRecurringQuery(
          {
            athenaRecurringQuery: {
              ...query,
              bindings: bindingsCopy,
              variables: [
                ...validationVariables,
                ...presetVariables,
                ...query.variables,
              ],
            },
            globalRecurringDateConfigs: params.globalRecurringDateConfigs,
          },
        );

        if (athenaRecurringQueryErrors.length) {
          toast({
            ...errorToastConfig,
            title: 'Bindings Update Error',
            description: athenaRecurringQueryErrors.join(', '),
          });
          return;
        }
      }

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: bindingIdxPropertyKey,
        updatedProperty: sanitizedBinding,
        setIsSaveLoading: setIsBindingEditSaving,
      });
    },
    [
      wipConfig,
      bindingIdxPropertyKey,
      query,
      queryType,
      presetVariables,
      validationVariables,
      toast,
      params,
      idx,
    ],
  );

  const deleteBinding = (): void => {
    if (!wipConfig?.isMutateAllowed(bindingIdxPropertyKey)) {
      return;
    }

    const bindings = getIn(wipConfig.formProps.values, bindingsPropertyKey);
    finleyAssert(
      _areBindings(bindings),
      `Retrieved non-binding structure when utilizing [key:${bindingsPropertyKey}]`,
    );

    wipConfig.formProps.setFieldValue(
      bindingsPropertyKey,
      bindings.filter((_unused, varIdx) => varIdx !== idx),
    );
  };

  if (wipConfig?.isMutateAllowed(bindingIdxPropertyKey) && isBindingEditOpen) {
    return (
      <EditBindingRow
        idx={idx}
        initialBindingString={bindingString}
        onSave={saveBinding}
        onClose={onBindingEditClose}
        isSaving={isBindingEditSaving}
      />
    );
  }

  return (
    <Tr>
      <Td>{idx + 1}</Td>
      <Td>
        <FormulaContainer>
          <Text>{bindingString}</Text>
        </FormulaContainer>
      </Td>
      {wipConfig?.isMutateAllowed(bindingIdxPropertyKey) ? (
        <Td>
          <CopyIconButton
            ariaLabel="duplicate binding"
            label="Duplicate Binding"
            onClick={() => {
              addBinding({ bindingStringInput: bindingString });
            }}
          />
          <EditIconButton
            ariaLabel="edit binding"
            label="Edit Binding"
            onClick={onBindingEditOpen}
          />
          <DeleteIconButton
            ariaLabel="delete binding"
            label="Delete Binding"
            onClick={() => deleteBinding()}
          />
        </Td>
      ) : (
        <Td />
      )}
    </Tr>
  );
};

type AddBindingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClickAdd: (params: { bindingStringInput: string }) => void;
};
const AddBindingModal = ({
  isOpen,
  onClose,
  onClickAdd,
}: AddBindingModalProps) => {
  const toast = useToast();
  const initialBindingFormula = '="Placeholder"';
  const [bindingString, setBindingString] = useState<string>(
    initialBindingFormula,
  );

  const onCloseModal = () => {
    onClose();
    setBindingString(initialBindingFormula);
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Binding</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color={colors.black[70]} mb="24px">
            Bindings starting with &quot;=&quot; will be evaluated as formula
          </Text>
          <Input
            type="text"
            value={bindingString}
            onChange={(e) => setBindingString(e.target.value)}
          />
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onCloseModal}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              if (initialBindingFormula) {
                await onClickAdd({
                  bindingStringInput: bindingString,
                });
                onCloseModal();
              } else {
                toast({
                  ...errorToastConfig,
                  id: 'add-threshold-modal-missing-info',
                  title: 'Fill in all required fields',
                });
              }
            }}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type BaseBindingContainerProps = {
  wipConfig?: WipConfig;
  bindings: string[];
  bindingsPropertyKey: string;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  query:
    | CreditAgreement.Config.AthenaConfig.Query
    | CreditAgreement.Config.AthenaConfig.RecurringQuery;
  queryType: 'singleQuery' | 'multiQuery';
};
interface AthenaQueryBindingContainerProps extends BaseBindingContainerProps {
  query: CreditAgreement.Config.AthenaConfig.Query;
  queryType: 'singleQuery';
}
interface AthenaRecurringQueryBindingContainerProps
  extends BaseBindingContainerProps {
  query: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  queryType: 'multiQuery';
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
}

export const BindingContainer = (
  params:
    | AthenaQueryBindingContainerProps
    | AthenaRecurringQueryBindingContainerProps,
) => {
  const {
    wipConfig,
    bindings,
    bindingsPropertyKey,
    presetVariables,
    validationVariables,
    query,
    queryType,
  } = params;

  const {
    isOpen: isAddBindingOpen,
    onOpen: onAddBindingOpen,
    onClose: onAddBindingClose,
  } = useDisclosure();

  const addBinding = useCallback(
    ({ bindingStringInput }: { bindingStringInput: string }): void => {
      if (!wipConfig?.isMutateAllowed(bindingsPropertyKey)) {
        return;
      }

      const wipBindings = getIn(
        wipConfig.formProps.values,
        bindingsPropertyKey,
      );
      finleyAssert(
        _areBindings(wipBindings),
        `Retrieved non-Athena Bindings structure for [key:${bindingsPropertyKey}]`,
      );

      const bindingsCopy = _.cloneDeep(wipBindings);
      bindingsCopy.push(bindingStringInput);

      wipConfig.formProps.setFieldValue(bindingsPropertyKey, bindingsCopy);
    },
    [wipConfig, bindingsPropertyKey],
  );

  return (
    <Box
      id="bindings-full-container"
      mt="32px"
      mb="24px"
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      <Text fontWeight={600} fontSize="18px" mb="16px">
        Bindings
      </Text>

      <Text color={colors.black[70]} mb="24px">
        Bindings starting with &quot;=&quot; will be evaluated as formula
      </Text>

      {wipConfig?.isMutateAllowed(bindingsPropertyKey) && (
        <Flex justifyContent="flex-end" mb="16px">
          <AddTextButton
            ariaLabel="add binding"
            label="Add Binding"
            onClick={() => onAddBindingOpen()}
          />
          <AddBindingModal
            isOpen={isAddBindingOpen}
            onClose={onAddBindingClose}
            onClickAdd={addBinding}
          />
        </Flex>
      )}

      <Box borderWidth="1px" maxH="50vh" overflow="scroll">
        <Table>
          <Thead>
            <Tr position="sticky" top="0" zIndex={1}>
              {/* @dani todo: replace with binding name instead of number when named bindings are implemented */}
              <Th w="15%">Index</Th>
              <Th w="70%">Formula</Th>
              {wipConfig?.isMutateAllowed(bindingsPropertyKey) && (
                <Th w="15%" />
              )}
            </Tr>
          </Thead>
          <Tbody>
            {bindings.length > 0 ? (
              bindings.map((binding, index) => {
                const key = `binding-${binding}-${index}`;
                if (queryType === 'singleQuery') {
                  return (
                    <BindingRow
                      key={key}
                      bindingString={binding}
                      wipConfig={wipConfig}
                      bindingsPropertyKey={bindingsPropertyKey}
                      idx={index}
                      addBinding={addBinding}
                      presetVariables={presetVariables}
                      validationVariables={validationVariables}
                      query={query}
                      queryType={queryType}
                    />
                  );
                }

                return (
                  <BindingRow
                    key={key}
                    bindingString={binding}
                    wipConfig={wipConfig}
                    bindingsPropertyKey={bindingsPropertyKey}
                    idx={index}
                    addBinding={addBinding}
                    presetVariables={presetVariables}
                    validationVariables={validationVariables}
                    query={query}
                    queryType={queryType}
                    globalRecurringDateConfigs={
                      params.globalRecurringDateConfigs
                    }
                  />
                );
              })
            ) : (
              <Tr>
                <Td colSpan={2}>No bindings configured.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
